import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useBrokerCommissionsDue, useBrokers } from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return currencyFormatter(num);
};

const rowLandlordName = (row) =>
  row.landlord_name ||
  row.related_landlord?.business_name ||
  `${row.related_landlord?.related_user?.first_name || ""} ${row.related_landlord?.related_user?.last_name || ""}`.trim() ||
  row.landlord_id ||
  "—";

const rowPropertyName = (row) =>
  row.property_name || row.rental_name || row.related_property?.rental_name || row.property_id || "—";

const BrokerCommissions = () => {
  const role = useSelector((state) => state.role);
  const brokerFromState = useSelector((state) => state.broker);
  const isBroker = role === "broker";
  const isStaff = role === "admin" || role === "manager";

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [brokerFilterId, setBrokerFilterId] = useState("");

  const { brokers, isLoadingBrokers } = useBrokers();
  const brokerIdForQuery = isBroker ? undefined : brokerFilterId ? Number(brokerFilterId) : undefined;

  const { commissionsDue, summary, isLoadingCommissionsDue, errorCommissionsDue } = useBrokerCommissionsDue({
    month,
    year,
    brokerId: brokerIdForQuery,
  });

  const years = useMemo(() => {
    const current = now.getFullYear();
    return [current - 1, current, current + 1];
  }, []);

  if (!isBroker && !isStaff) {
    return (
      <Box m={2}>
        <Alert severity="warning">You do not have access to broker commissions.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        Commissions Due
      </Typography>
      {isBroker && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {brokerFromState?.broker_name
            ? `Broker: ${brokerFromState.broker_name}`
            : "Your commission balance by assigned landlord and property."}
        </Typography>
      )}

      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isStaff && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Broker (staff)</InputLabel>
            <Select
              label="Broker (staff)"
              value={brokerFilterId}
              onChange={(e) => setBrokerFilterId(e.target.value)}
            >
              <MenuItem value="">All / default</MenuItem>
              {(brokers || []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.broker_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {errorCommissionsDue && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorCommissionsDue}
        </Alert>
      )}

      {summary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Total due: {formatMoney(summary.total_due ?? summary.balance_due)} · Paid:{" "}
          {formatMoney(summary.total_paid)} · Balance: {formatMoney(summary.balance_due ?? summary.total_balance)}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingCommissionsDue || (isStaff && isLoadingBrokers) ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Landlord</TableCell>
                <TableCell>Property</TableCell>
                <TableCell align="right">Gross collected</TableCell>
                <TableCell align="right">Rate (%)</TableCell>
                <TableCell align="right">Commission</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commissionsDue.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No commission data for this period.</TableCell>
                </TableRow>
              ) : (
                commissionsDue.map((row, index) => (
                  <TableRow key={row.id ?? `${row.landlord_id}-${row.property_id}-${index}`}>
                    <TableCell>{rowLandlordName(row)}</TableCell>
                    <TableCell>{rowPropertyName(row)}</TableCell>
                    <TableCell align="right">{formatMoney(row.gross_collected)}</TableCell>
                    <TableCell align="right">
                      {row.broker_commission_rate ?? row.commission_rate ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(row.commission_due ?? row.gross_commission)}
                    </TableCell>
                    <TableCell align="right">{formatMoney(row.paid ?? row.amount_paid)}</TableCell>
                    <TableCell align="right">{formatMoney(row.balance ?? row.balance_due)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default BrokerCommissions;
